import React, { useState } from 'react';
import { Check, ShieldCheck, Zap, Lock, Package, Star } from 'lucide-react';
const CHECKOUT_URLS = {
  esencial: 'https://checkout.digsol.shop/cart/45227430281398:1',
  total: 'https://checkout.digsol.shop/cart/45227431166134:1'
};
type PlanId = 'esencial' | 'total';
export function Pricing({
  isFinal = false
}: {
  isFinal?: boolean;
}) {
  // Pack Total (higher price) preselected
  const [selected, setSelected] = useState<PlanId>('total');
  return <section id={!isFinal ? 'precios' : undefined} className={`scroll-mt-8 px-6 lg:px-8 ${isFinal ? 'bg-white pb-24 pt-4' : 'bg-white py-20'}`} data-id="element-150">
      <div className="max-w-[900px] mx-auto" data-id="element-151">
        {!isFinal && <div className="text-center mb-10" data-id="element-152">
            <h2 className="text-3xl md:text-5xl font-extrabold mb-4" data-id="element-153">
              Elegí tu Aprobá en serio
            </h2>
            <p className="text-lg text-dark/70 font-medium" data-id="element-154">
              Ambas opciones incluyen los 4 bonos estratégicos completos
            </p>
          </div>}

        {/* Compact selectable options — both visible without scrolling */}
        <div role="radiogroup" aria-label="Elegí tu pack" className="grid grid-cols-2 gap-3 md:gap-5 items-stretch mb-6" data-id="element-155">
          <PlanOption id="esencial" selected={selected === 'esencial'} onSelect={() => setSelected('esencial')} selectedBorder="salmon" title="PACK ESENCIAL" summary="6 tomos + 4 bonos" oldPrice="$27.990" price="$19.990" features={['6 tomos completos', '4 bonos estratégicos', 'Acceso inmediato']} data-id="element-156" />

          <PlanOption id="total" selected={selected === 'total'} onSelect={() => setSelected('total')} selectedBorder="lavender" title="PACK TOTAL" summary="8 tomos + 6 bonos" oldPrice="$47.990" price="$29.990" featured badge="⭐ MÁS ELEGIDO" features={['Todo lo del Pack Esencial', '+ Tomo 7 · Cuando reprobás', '+ Tomo 8 · Estudio sostenible', '+ 2 bonos exclusivos']} data-id="element-157" />
        </div>

        {/* Shared purchase button — redirects based on selection */}
        <a href={CHECKOUT_URLS[selected]} className="block w-full text-center bg-salmon hover:bg-salmon/90 text-dark font-extrabold text-lg md:text-xl py-5 rounded-full shadow-lg transform transition hover:scale-[1.02]" data-id="element-158">
          {selected === 'total' ? '💥 COMPRAR PACK TOTAL — $29.990' : 'COMPRAR PACK ESENCIAL — $19.990'}
        </a>

        {selected === 'total' && <p className="text-center text-xs md:text-sm text-dark/60 font-medium mt-3 px-4" data-id="element-159">
            La opción más elegida por estudiantes que quieren aprobar esta época
            y no volver a pasar por esto nunca más
          </p>}

        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs md:text-sm font-semibold text-dark/70 mt-6" data-id="element-160">
          <div className="flex items-center gap-2" data-id="element-161">
            <Lock size={16} data-id="element-162" /> Pago seguro
          </div>
          <div className="flex items-center gap-2" data-id="element-163">
            <Zap size={16} data-id="element-164" /> Acceso en 5 min
          </div>
          <div className="flex items-center gap-2" data-id="element-165">
            <ShieldCheck size={16} data-id="element-166" /> Garantía 7 días
          </div>
          <div className="flex items-center gap-2" data-id="element-167">
            <Package size={16} data-id="element-168" /> Mercado Pago
          </div>
        </div>
      </div>
    </section>;
}
function PlanOption({
  id,
  selected,
  onSelect,
  selectedBorder,
  title,
  summary,
  oldPrice,
  price,
  features,
  featured = false,
  badge
}: {
  id: PlanId;
  selected: boolean;
  onSelect: () => void;
  selectedBorder: 'salmon' | 'lavender';
  title: string;
  summary: string;
  oldPrice: string;
  price: string;
  features: string[];
  featured?: boolean;
  badge?: string;
}) {
  // Border width is CONSTANT (4px) at all times so selecting a card never
  // shifts layout — only the border COLOR changes between states.
  const borderColorClass = selected ? selectedBorder === 'lavender' ? 'border-lavender' : 'border-salmon' : 'border-border';
  const checkBgClass = selectedBorder === 'lavender' ? 'bg-lavender border-lavender' : 'bg-salmon border-salmon';
  return <button type="button" role="radio" aria-checked={selected} onClick={onSelect} className={`relative box-border text-left rounded-2xl p-4 md:p-6 flex flex-col min-h-[320px] md:min-h-[380px] border-4 ${borderColorClass} transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-salmon/60 ${selected ? 'bg-white shadow-xl' : 'bg-cream shadow-sm'}`} data-id="element-169">
      {badge && <span className={`absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] md:text-xs font-extrabold px-3 py-1 rounded-full shadow-sm ${selected ? 'bg-lavender text-dark' : 'bg-lavender/70 text-dark'}`} data-id="element-170">
          {badge}
        </span>}

      {/* Selection indicator — always rendered to reserve space; only opacity changes */}
      <span className={`absolute top-3 right-3 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${selected ? `${checkBgClass} opacity-100` : 'border-border bg-white opacity-40'}`} aria-hidden="true" data-id="element-171">
        {selected && <Check size={12} className="text-dark" strokeWidth={3} data-id="element-172" />}
      </span>

      <h3 className={`font-extrabold mb-1 mt-2 ${featured ? 'text-lg md:text-2xl' : 'text-base md:text-xl'}`} data-id="element-173">
        {title}
      </h3>
      <p className="text-dark/60 text-xs md:text-sm font-medium mb-3" data-id="element-174">
        {summary}
      </p>

      <div className="mb-3" data-id="element-175">
        <p className="text-dark/40 line-through text-xs md:text-sm font-bold leading-none mb-1" data-id="element-176">
          {oldPrice}
        </p>
        <p className={`font-extrabold leading-none ${featured ? 'text-3xl md:text-5xl text-salmon' : 'text-2xl md:text-4xl text-dark'}`} data-id="element-177">
          {price}
        </p>
      </div>

      <ul className="space-y-1.5 mt-auto" data-id="element-178">
        {features.map((f, i) => <li key={i} className="flex items-start gap-2 text-xs md:text-sm text-dark/80" data-id="element-179">
            <Check size={14} className="text-green-500 shrink-0 mt-0.5" aria-hidden="true" data-id="element-180" />
            <span data-id="element-181">{f}</span>
          </li>)}
      </ul>
    </button>;
}